"use client"
import React, { useState, useEffect } from "react";
import EmbeddedMapView from "@/components/maps/embed/EmbeddedMapView";
import EmbeddedLayerView from "@/components/maps/embed/EmbeddedLayerView";
import EmbeddedPointCardWrapper from "@/components/maps/embed/EmbeddedPointCardWrapper";
import { useMediaQuery } from "react-responsive";

import "@/components/maps/embed/EmbeddedMapViewWrapper.css";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";
import CommunityFeatureProfile from "@/components/communities/CommunityFeatureProfile";
import CommunityMapPointFilterv2 from "@/components/communities/CommunityMapPointFilterv2";
import TagsFilter from "@/components/shared/TagsFilter";
import PinLayerPointPopup from "@/components/assetmanagement/PinLayerPointPopup";

const EmbeddedMapViewWrapper = ({
  name,
  className,
  map,
  community,
  overrideLayers = null,
  points,
  width,
  height,
  addMyLocation,
  tagFilter,
  searchFilter,
  rightControls,
  external,
  featureGroups,
  routes,
  pinLayers,
  selectedWaypoint,
  focusOn,
  defaultZoom,
  enablePointView = true,
  enableFeatureView = true,
  enableLayerView,
  onMapClickCallback,
  onMarkerClickCallback,
  onFeatureClickCallback,
  onPinLayerClickCallback,
  wrapperRef,
  defaultCenter,
  mapCenterPadding
}) => {
  const isSm = useMediaQuery({ maxWidth: "767.98px" });

  const locationState = null;

  const [layers, setLayers] = useState(null);
  const [enabledLayers, setEnabledLayers] = useState(null);
  const [enabledFeatureGroups, setEnabledFeatureGroups] = useState(null);
  const [enabledPinLayers, setEnabledPinLayers] = useState(null);
  const [showLayerView, setShowLayerView] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filteredPoints, setFilteredPoints] = useState(null);
  const [pointFilter, setPointFilter] = useState(null);

  const [point, setPoint] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [showPointDetails, setShowPointDetails] = useState(false);
  const [showSearchFilter, setShowSearchFilter] = useState(false);

  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationUnavailable, setLocationUnavailable] = useState(false);
  const [mapElement, setMapElement] = useState(null);
  const [subLayersEnabled, setSubLayersEnabled] = useState(false);
  const mapToggle = { pins: true, features: false, assetGroups: true };

  const [mapPopup, setMapPopup] = useState(null);

  const [routeFeatures, setRouteFeatures] = useState([]);

  const [selectedPinLayerPoint, setSelectedPinLayerPoint] = useState(null);

  const [horizontalOffset, setHorizontalOffset] = useState(0);

  useEffect(() => {
    setHorizontalOffset(screen.width * 0.25);
  }, []);

  useEffect(() => {
    setShowSearchFilter(community != null && searchFilter);
  }, [community]);

  useEffect(() => {
    if (routes) {
      setRouteFeatures(routes.map(n => n.feature));
    }
  }, [routes]);

  const mapWrapper = element => {
    if (element) {
      setMapElement(element);
    }
  };

  const createLocationList = (points, hasSubLayers) => {
    let locationList = [];
    points.forEach(point => {
      var createMapLocation = point && point.location;
      community != null;
      if (createMapLocation) {
        var locationInfo = {};
        locationInfo["latitude"] = point.location.latitude;
        locationInfo["longitude"] = point.location.longitude;
        locationInfo["id"] = point.id;
        locationInfo["markerImageId"] = null;
        locationInfo["layer"] = hasSubLayers
          ? point.subLayer ?? point.layer
          : point.layer ?? "DEFAULT";
        locationInfo["label"] = point.title;
        locationList.push(locationInfo);
      }
    });
    return locationList;
  };

  useEffect(() => {
    if (map && points) {
      var theLayers = overrideLayers ?? map.layers;
      if (theLayers && theLayers.length > 0) {
        const hasSubLayers =
          theLayers.findIndex(layer => layer.value.startsWith("BUSINESSES.")) >
          -1;
        setSubLayersEnabled(hasSubLayers);
        setVisibleLayers(map.layers, overrideLayers);
        if (points != null) {
          setLocations(createLocationList(points, hasSubLayers));
        }
      } else if (points != null) {
        setLocations(createLocationList(points));
      }
    }
  }, [map, points, overrideLayers]);

  useEffect(() => {
    if (locationState && locationState.error) {
      setLocationUnavailable(true);
      setCurrentLocation(null);
    } else if (
      locationState &&
      locationState.latitude != null &&
      locationState.longitude != null
    ) {
      var currentLocation = {
        name: "Current Location",
        latitude: locationState.latitude,
        longitude: locationState.longitude,
        locationSource: "GPS"
      };
      setCurrentLocation(currentLocation);
    }
  }, [locationState]);

  useEffect(() => {
    if (featureGroups && featureGroups.length > 0) {
      setEnabledFeatureGroups(featureGroups.map(fg => fg.id));
    }
  }, [featureGroups]);

  useEffect(() => {
    if (pinLayers && pinLayers.length > 0) {
      setEnabledPinLayers(pinLayers.map(pl => pl.id));
    }
  }, [pinLayers]);

  const setVisibleLayers = (layers, override) => {
    setLayers(override ?? layers);
    var layersToEnable = override ? override : layers;
    var enabledLayers = layersToEnable.map(function(layer, index) {
      return layer.value;
    });
    setEnabledLayers(enabledLayers);
  };

  useEffect(() => {
    if (enabledLayers) {
      var myPoints = pointFilter
        ? points.filter(pt => pointFilter.includes(pt.id))
        : points;
      setFilteredPoints(filterPointsFromEnabledLayers(myPoints, enabledLayers));
    }
  }, [enabledLayers]);

  const filterPointsFromEnabledLayers = (points, layers) => {
    const layerFilteredPoints = points.filter(
      pt =>
        layers.includes(pt.layer) ||
        (pt.subLayer && layers.includes(pt.subLayer))
    );
    return layerFilteredPoints;
  };

  useEffect(() => {
    var selectedLoc = locations.find(l => l.id === focusOn);
    setSelectedMarker(selectedLoc);
  }, [focusOn]);

  useEffect(() => {
    if (selectedMarkerId) {
      if (points.length > 0) {
        var mapPoint = points.find(m => m.id === selectedMarkerId);
        setPoint(mapPoint);
        var selectedLoc = locations.find(l => l.id === selectedMarkerId);
        setSelectedMarker(selectedLoc);
        if (enablePointView) {
          setShowPointDetails(true);
        }
      } else {
        setSelectedMarker(null);
      }
    } else {
      setSelectedMarker(null);
    }
  }, [selectedMarkerId]);

  const handleForwardArrowClick = () => {
    var myPoints = filteredPoints ?? points;
    var currentIndex = myPoints.findIndex(loc => loc.id === selectedMarkerId);
    var length = myPoints.length;
    var newIndex = (currentIndex + 1) % length;
    var newlySelected = myPoints[newIndex];
    setSelectedMarkerId(newlySelected.id);
  };

  const handleBackwardArrowClick = () => {
    var myPoints = filteredPoints ?? points;
    var currentIndex = myPoints.findIndex(loc => loc.id === selectedMarkerId);
    var length = myPoints.length;
    var newIndex = currentIndex === 0 ? length - 1 : currentIndex - 1;
    var newlySelected = myPoints[newIndex];
    setSelectedMarkerId(newlySelected.id);
  };

  const onLocationClicked = marker => {
    setSelectedFeature(null);
    setSelectedPinLayerPoint(null);
    setSelectedMarkerId(marker.id);
    if (onMarkerClickCallback) {
      onMarkerClickCallback(marker);
    }
  };

  const onFeatureClicked = (type, featureGroupId, id, lngLat) => {
    setSelectedMarkerId(null);
    if (enableFeatureView && lngLat) {
      const featureGroup = featureGroups.find(fg => fg.id === featureGroupId);
      const feature = featureGroup?.features?.find(n => n.id === id);
      if (feature) {
        setMapPopup(
          <MapBoxMapPopup latitude={lngLat.lat} longitude={lngLat.lng}>
            <CommunityFeatureProfile feature={feature} verticalLayout={true} cta={true} />
          </MapBoxMapPopup>
        );
      }
    }
    if (onFeatureClickCallback) {
      onFeatureClickCallback(type, featureGroupId, id);
    }
  };

  const onAssetGroupPointClicked = (evt, pinLayerId, point) => {
    setSelectedMarkerId(null);
    var pinLayer = pinLayers.find(pl => pl.id === pinLayerId);
    if (pinLayer) {
      var selectedPinLayerPoint = pinLayer.points.find(p => p.id === point.id);
      if (selectedPinLayerPoint) {
        var popup = (
          <PinLayerPointPopup pinLayerPoint={selectedPinLayerPoint} />
        );
        setMapPopup(popup);
      }
      setSelectedPinLayerPoint(selectedPinLayerPoint);
    }
    if (onPinLayerClickCallback) {
      onPinLayerClickCallback(pinLayerId, point.id);
    }
  };

  const onMapClicked = (lat, lon) => {
    setMapPopup(null);
    setSelectedMarkerId(null);
    setShowPointDetails(false);
    if (onMapClickCallback) {
      onMapClickCallback();
    }
  };

  return (
    <div
      className={`embedded-map-view-wrapper ${className}`}
      ref={wrapperRef ?? mapWrapper}
    >
      {map && (
        <EmbeddedMapView
          addMyLocation={addMyLocation}
          type="MAP"
          selectedLayers={enabledLayers}
          layers={layers}
          locations={locations}
          filter={pointFilter}
          selectedMarker={selectedMarker}
          onLocationClicked={onLocationClicked}
          onMapClicked={onMapClicked}
          onFeatureClicked={(type, featureGroupId, feature, opt) => {
            onFeatureClicked(type, featureGroupId, feature.id, opt);
          }}
          height={height ? height : "200px"}
          width={width ? width : "100vw"}
          featureGroups={featureGroups}
          selectedFeatureGroups={enabledFeatureGroups}
          assetGroups={pinLayers}
          selectedPinLayers={enabledPinLayers}
          onAssetGroupPointClicked={(evt, pinLayerId, point) => {
            onAssetGroupPointClicked(evt, pinLayerId, point);
          }}
          routes={routeFeatures}
          mapToggle={mapToggle}
          selectedWaypoint={selectedWaypoint}
          horizontalOffset={isSm ? 0 : -horizontalOffset}
          defaultZoom={defaultZoom}
          defaultCenter={defaultCenter}
          mapCenterPadding={mapCenterPadding}
          popupObject={mapPopup}
        />
      )}
      <div id="map-controls" className={`map-controls${showLayerView ? " push" : " pull"}`}>
        {layers && enableLayerView && (
          <>
            {(!isSm || (isSm && !showLayerView)) && (
              <div
                className={
                  "layer-view-button" + (showLayerView ? " active" : "")
                }
                onClick={() => {
                  setShowPointDetails(false);
                  setSelectedMarkerId(null);
                  setShowLayerView(!showLayerView);
                }}
              >
                <span className="material-icons">layers</span>
              </div>
            )}
          </>
        )}
        {tagFilter && (
          <div className="embedded-map-tag-filter">
            <TagsFilter
              points={points}
              onSelect={filteredPoints => {
                if (filteredPoints) {
                  setPointFilter(filteredPoints.map(fp => fp.id));
                } else {
                  setPointFilter(null);
                }
              }}
              scrollable={isSm}
            />
          </div>
        )}
        {showSearchFilter && !showLayerView && (
          <div className="map-search-filter">
            <CommunityMapPointFilterv2
              className="style-for-map"
              points={points}
              types={["BUSINESS", "POI", "AMBASSADOR"]}
              onSelect={filteredPoints => {
                if (filteredPoints) {
                  setPointFilter(filteredPoints.map(pt => pt.id));
                  if (enabledLayers && enabledLayers.length > 0) {
                    filteredPoints = filterPointsFromEnabledLayers(
                      filteredPoints,
                      enabledLayers
                    );
                  }
                  setFilteredPoints(filteredPoints);
                  setSelectedMarkerId(null);
                  setShowPointDetails(false);
                  setShowLayerView(false);
                  if (filteredPoints.length === 1) {
                    setPoint(filteredPoints[0]);
                    if (enablePointView) {
                      setShowPointDetails(true);
                    }
                  }
                } else {
                  setPointFilter(null);
                  setFilteredPoints(null);
                  setSelectedMarkerId(null);
                  setShowPointDetails(false);
                  setShowLayerView(false);
                }
              }}
            />
          </div>
        )}
      </div>
      {layers && enableLayerView && (
        <EmbeddedLayerView
          showLayerView={showLayerView && !showPointDetails}
          onClose={() => setShowLayerView(false)}
          layers={overrideLayers ? overrideLayers : layers}
          enabledLayers={enabledLayers}
          onLayerChange={layers => setEnabledLayers(layers)}
        />
      )}
      {enablePointView && (
        <EmbeddedPointCardWrapper
          name={name}
          coordinates={currentLocation}
          showPointDetails={showPointDetails}
          closeMarkerDetails={() => {
            setSelectedMarkerId(null);
            setShowPointDetails(false);
          }}
          point={point}
          ownerId={map ? map.owner.id : null}
          contentId={map ? map.id : null}
          community={community}
          backClick={handleBackwardArrowClick}
          forwardClick={handleForwardArrowClick}
          parent={mapElement}
          external={external}
        />
      )}
      {rightControls}
    </div>
  );
};
export default EmbeddedMapViewWrapper;
